// ── Повторение карточек (FSRS, 4 ответа) ─────────────────────────────────────
// Один экран повторения для всего приложения: главная, раздел, проект, плеер.
// Подходы по 10 карточек, в конце — итог и «Ещё N» / «Хватит на сегодня».

import { useEffect, useRef, useState } from "react";
import { backLinkStyle } from "../components/Bits";
import { readStreak } from "../components/Today";
import { t } from "../i18n";
import Icon from "../Icon";
import { markSetting } from "../settings";
import { speak, stopSpeaking } from "../speech";
import { buildStudyQueue, dayProgress, getDailyStats, getFsrsSettings, getNewMix, markReviewedToday, recordDailyReview, sortByDeckOrder, srsDueLabel, srsNext, srsNextLabel, staysInSession } from "../srs";
import { FONT, SANS, cardColorIn, themeKit } from "../themes";
import { Field, Modal, Button as UiButton, IconButton as UiIconButton } from "../ui";
import { labelOn, legibleOn, shade } from "../ui/color";
import { juice } from "../arcadeTable";
import { answerSound, cardSound, celebrate } from "../arcadeJuice";
import { LeechNudge } from "../games/GamesEntry";
import { nCards, nDays, recordActivity, resolveLang } from "../utils";
import { usePalette } from "../ui/theme";
import { panelSurface } from '../ui/surface';

// ── QuizScreen — квиз и SRS-повторение ───────────────────────────────────────

// Текст карточки: короткое слово — крупно, длинная фраза — мельче, а целый абзац —
// слева и с прокруткой внутри карточки (раньше длинный текст вылезал за её края).
export const cardText=(t,base)=>{
  const n=(t||"").length;
  const fs=n<=20?base:n<=60?Math.round(base*0.8):n<=160?Math.round(base*0.64):16;
  return{fontFamily:FONT,fontSize:fs,lineHeight:n>60?1.55:1.25,width:"100%",overflowWrap:"anywhere",wordBreak:"break-word",whiteSpace:"pre-wrap",
    textAlign:n>160?"left":"center",...(n>160?{maxHeight:"45vh",overflowY:"auto"}:{})};
};
export const cardSmall={width:"100%",overflowWrap:"anywhere",wordBreak:"break-word",maxHeight:"18vh",overflowY:"auto"};

// Повторение открылось первым экраном после перезагрузки страницы (F5, жест «обновить»)?
// Тогда начатый подход продолжаем; любое следующее открытие — уже обычный новый подход.
let firstQuizMount=true;
function isReloadResume(){
  const first=firstQuizMount;firstQuizMount=false;
  try{return first&&performance.getEntriesByType("navigation")[0]?.type==="reload";}catch{return false;}
}

// Масти игральной карты по кругу (♥ ♠ ♦ ♣) — для «Аркады».
const SUIT=[{s:"♥",c:"#e2372f"},{s:"♠",c:"#1d1b26"},{s:"♦",c:"#e2372f"},{s:"♣",c:"#1d1b26"}];
// Достоинство в углу карты и классический шрифт карт (системный, с засечками).
const RANK=["A","K","Q","J","10","9","8","7"];

export function QuizScreen({allProjects,projectId,autoSrs,theme,onTheme,onBack,onGames,onSaveProject,onOpenFsrsSettings}){
  const P=usePalette();
  // Что повторяем: все колоды (языка) или выбранный проект — одна и та же очередь.
  const srcMode=projectId?"project":"all";
  const [selIds]=useState(()=>new Set(!projectId?[]:Array.isArray(projectId)?projectId:[projectId]));
  const [mode,setMode]=useState("choose");
  const dir="word2trans"; // лицевая сторона — слово, обратная — перевод
  // SRS state
  const [srsCards,setSrsCards]=useState([]); // working copy with updated srs fields
  const [srsIdx,setSrsIdx]=useState(0);
  const [srsFlipped,setSrsFlipped]=useState(false);
  const [srsDone,setSrsDone]=useState(0);
  // Серия правильных ответов подряд — «множитель», как в Balatro (видно и слышно в «Аркаде»).
  const [combo,setCombo]=useState(0);
  // «Аркада»: шорох раздачи на новую карту и переворота на ответ (в других темах звуков нет).
  useEffect(()=>{if(mode==="srs")cardSound("deal");},[srsIdx,mode]);
  useEffect(()=>{if(srsFlipped)cardSound("flip");},[srsFlipped]);
  const [editingCard,setEditingCard]=useState(false);
  const [editWord,setEditWord]=useState("");
  const [editTrans,setEditTrans]=useState("");
  // ── FSRS-настройки: live, обновляются при сохранении в модалке/sync с сервера
  const [fsrs,setFsrs]=useState(()=>getFsrsSettings());
  // dailyStats — счётчик сделанных за сегодня (Anki-style лимиты). Обновляется
  // после каждой оценки в SRS-режиме И при cross-device sync.
  const [,setDailyStats]=useState(()=>getDailyStats(getFsrsSettings().dayStart));
  useEffect(()=>{
    const refresh=()=>{const s=getFsrsSettings();setFsrs(s);setDailyStats(getDailyStats(s.dayStart));};
    const onUpd=(e)=>{
      const keys=e?.detail?.keys||[];
      if(!keys.length||keys.some(k=>k==='ac_fsrs_retention'||k==='ac_day_start_hour'||k==='ac_new_limit'||k==='ac_review_limit'||k==='ac_fsrs_daily'))refresh();
    };
    window.addEventListener('ac:settings-updated',onUpd);
    return()=>window.removeEventListener('ac:settings-updated',onUpd);
  },[]);
  const getCards=()=>srcMode==="all"?allProjects.flatMap(p=>p.cards||[]):allProjects.filter(p=>selIds.has(p.id)).flatMap(p=>p.cards||[]);

  const roundSize=()=>10;
  const startSrs=()=>{
    // Очередь как в Anki (см. buildStudyQueue): изучаемые → повторения по сроку,
    // новые — по порядку колод, внутри колоды — по месту в аудио. Раньше всё
    // перемешивалось случайно.
    const rank=new Map();
    sortByDeckOrder(allProjects).forEach((p,i)=>(p.cards||[]).forEach(c=>rank.set(c.id,i)));
    // Anki-style daily limits: учитываем УЖЕ сделанные за сегодня (per-day counter)
    const stats=getDailyStats(fsrs.dayStart);
    const {queue}=buildStudyQueue(getCards(),{
      deckRank:c=>rank.has(c.id)?rank.get(c.id):1e9,
      dayStart:fsrs.dayStart,
      newQuota:    fsrs.newLimit>0    ? Math.max(0, fsrs.newLimit    - stats.newDone)    : Infinity,
      reviewQuota: fsrs.reviewLimit>0 ? Math.max(0, fsrs.reviewLimit - stats.reviewDone) : Infinity,
      newMix:getNewMix(),
    });
    // Подход — короткий, как урок (10 карточек). Весь день разом не вываливаем.
    const round=queue.slice(0,roundSize());
    setSrsCards(round);setSrsIdx(0);setSrsFlipped(false);setSrsDone(0);setMode(round.length?"srs":"srs_done");
  };
  // Cancel TTS on unmount
  useEffect(()=>()=>stopSpeaking(),[]);

  // TTS
  const TTS_KEY='ac_tts_mode';
  const [ttsMode,setTtsMode]=useState(()=>localStorage.getItem(TTS_KEY)||'front');
  const cycleTts=()=>{const n=ttsMode==='front'?'back':ttsMode==='back'?'off':'front';setTtsMode(n);localStorage.setItem(TTS_KEY,n);};
  const ttsIcon=ttsMode==='off'?<Icon n="mute"/>:<span style={{display:'inline-flex',alignItems:'center',gap:2}}><Icon n="volume"/>{ttsMode==='front'?'↑':'↓'}</span>;
  const projOf=(card)=>card?allProjects.find(p=>(p.cards||[]).some(c=>c.id===card.id)):null;
  // Слово — голосом изучаемого языка, перевод — языком перевода (раньше русский перевод
  // читался японским голосом). Язык перевода не указан — угадываем по буквам.
  const sayWord=(card)=>speak(card?.word,projOf(card)?.src_lang||'');
  const sayTranslation=(card)=>speak(card?.translation,projOf(card)?.tgt_lang||'');
  const sayFront=(card)=>dir==='word2trans'?sayWord(card):sayTranslation(card);
  const sayBack=(card)=>dir==='word2trans'?sayTranslation(card):sayWord(card);
  // Auto-play front when advancing to new SRS card
  useEffect(()=>{
    if(srsCard&&ttsMode==='front')sayFront(srsCard);
  },[srsIdx]);// eslint-disable-line
  // Auto-play front when entering SRS mode
  useEffect(()=>{
    if(mode==='srs'&&srsCard&&ttsMode==='front')sayFront(srsCard);
  },[mode]);// eslint-disable-line
  // Auto-play back on SRS flip
  useEffect(()=>{
    if(srsFlipped&&srsCard&&ttsMode==='back')sayBack(srsCard);
  },[srsFlipped]);// eslint-disable-line

  // ── Persist & Restore ─────────────────────────────────────────────────────
  // Key insight: persist effect must NOT fire before restore completes.
  // If it fires first with mode="choose", it deletes the saved state → restore finds nothing.
  // Solution: gate the persist effect with `restoreDone` state (not a ref — needs to re-trigger persist).
  const QUIZ_KEY=`ac_quiz_${Array.isArray(projectId)?projectId.join(","):projectId||"all"}`;
  const [restoreDone,setRestoreDone]=useState(false);

  // PERSIST — runs only after restore has been attempted
  useEffect(()=>{
    if(!restoreDone)return; // block until restore runs once
    if(mode==="srs"){
      try{sessionStorage.setItem(QUIZ_KEY,JSON.stringify({mode,cardIds:srsCards.map(c=>c.id),idx:srsIdx,flipped:srsFlipped,srcMode}));}catch{}
    } else {
      try{sessionStorage.removeItem(QUIZ_KEY);}catch{}
    }
  },[mode,srsIdx,srsFlipped,srsCards,restoreDone]);

  // ── Запуск и продолжение после перезагрузки ─────────────────────────────────
  // Карточки после перезагрузки страницы приходят не сразу. Раньше подход собирался в
  // первый же миг — из пустого списка — и показывалось «Сейчас повторять нечего», хотя
  // подход не закончен. Теперь ждём карточки (не дольше 3 с — вдруг их правда нет), затем:
  // страницу перезагрузили посреди подхода — продолжаем его с той же карточки
  // (sessionStorage); открыли «Повторить» заново — собираем новый подход.
  const _restoredRef=useRef(false);
  const [waitedLong,setWaitedLong]=useState(false);
  useEffect(()=>{const id=setTimeout(()=>setWaitedLong(true),3000);return()=>clearTimeout(id);},[]);
  useEffect(()=>{
    if(_restoredRef.current)return;
    if(!allProjects.length&&!waitedLong)return;
    _restoredRef.current=true;
    const reload=isReloadResume(); // спрашиваем всегда: флаг «первый показ» тратится один раз
    const resume=!autoSrs||reload;
    let didRestore=false;
    try{
      const saved=resume?JSON.parse(sessionStorage.getItem(QUIZ_KEY)||"null"):null;
      if(saved&&saved.mode==="srs"&&saved.cardIds&&saved.cardIds.length){
        const idMap=Object.fromEntries(allProjects.flatMap(p=>p.cards||[]).map(c=>[String(c.id),c]));
        const restored=saved.cardIds.map(id=>idMap[String(id)]).filter(Boolean);
        if(restored.length){
          setSrsCards(restored);setSrsIdx(Math.min(saved.idx||0,restored.length-1));
          setSrsFlipped(!!saved.flipped);setMode("srs");didRestore=true;
        }
      }
    }catch{}
    // Restore попробован — теперь можно сохранять (persist выше ждёт restoreDone).
    setRestoreDone(true);
    if(!didRestore){
      try{sessionStorage.removeItem(QUIZ_KEY);}catch{}
      if(autoSrs)startSrs();
    }
  },[allProjects,waitedLong]); // eslint-disable-line

  // SRS rating handler
  const rateSrs=(rating)=>{
    stopSpeaking();
    // Отклик стола «Аркады» цветом ответа (в других темах событие никто не слушает).
    const kind=rating===1?"again":rating===2?"hard":rating===4?"easy":"good";
    const nextCombo=rating===1?0:rating===2?combo:combo+1;
    setCombo(nextCombo);
    juice(kind,nextCombo);answerSound(kind,nextCombo);
    const card=srsCards[srsIdx];
    const wasNew=(card.srs_state||"new")==="new";
    const updated=srsNext(card,rating,{retention:fsrs.retention});
    // Persist: find project that owns this card and update it
    const proj=allProjects.find(p=>(p.cards||[]).some(c=>c.id===card.id));
    if(proj&&onSaveProject){
      const newCards=(proj.cards||[]).map(c=>c.id===card.id?updated:c);
      onSaveProject(proj.id,newCards);
    }
    recordActivity(1,resolveLang(proj?.src_lang));
    // Anki-style daily counter — для лимитов "X новых / Y повторений в день"
    const newStats=recordDailyReview(wasNew,fsrs.dayStart);
    markReviewedToday(card.id,fsrs.dayStart);try{markSetting('ac_reviewed_today');}catch{}
    setDailyStats(newStats);
    try{markSetting('ac_fsrs_daily');}catch{}
    const newList=srsCards.map((c,i)=>i===srsIdx?updated:c);
    // «Не знаю» / шаг обучения: карточка вернётся в это же занятие, примерно когда
    // подойдёт её срок (≈10 секунд на карточку), а если карточек меньше — в конец.
    if(staysInSession(updated)){
      const gap=Math.max(2,Math.round((new Date(updated.srs_due).getTime()-Date.now())/10000));
      newList.splice(Math.min(newList.length,srsIdx+1+gap),0,updated);
    }
    setSrsCards(newList);setSrsDone(d=>d+1);
    if(srsIdx+1>=newList.length){
      // План дня закрыт этим подходом — джекпот: монеты и надпись (в «Аркаде»).
      const left=dayProgress(getCards()).total;
      celebrate(left<=1,t("Джекпот!"));
      setMode("srs_done");
    }else{setSrsIdx(i=>i+1);setSrsFlipped(false);}
  };

  // Клавиши как в Anki: пробел/Enter — показать ответ, 1·2·3 — оценка (пробел после ответа = «Знаю»).
  // В просмотре карточек: пробел — перевернуть, ←/→ — листать. Без массива зависимостей,
  // чтобы обработчик всегда видел свежее состояние.
  useEffect(()=>{
    const onKey=(e)=>{
      if(e.ctrlKey||e.metaKey||e.altKey||e.target?.closest?.("input,textarea,select,[contenteditable=true]"))return;
      const go=(fn)=>{e.preventDefault();fn();};
      if(mode==="srs"&&!editingCard&&srsCards[srsIdx]){
        if(!srsFlipped&&(e.key===" "||e.key==="Enter"))go(()=>setSrsFlipped(true));
        else if(srsFlipped&&(e.key==="1"||e.key==="2"||e.key==="3"||e.key==="4"))go(()=>rateSrs(+e.key));
        else if(srsFlipped&&(e.key===" "||e.key==="Enter"))go(()=>rateSrs(3));
      }
    };
    window.addEventListener("keydown",onKey);
    return()=>window.removeEventListener("keydown",onKey);
  });

  const srsCard=srsCards[srsIdx];
  // Сохранить правку карточки из окна «Редактировать карточку»: в подходе и в её проекте.
  const saveEdit=()=>{
    const updated={...srsCard,word:editWord.trim()||srsCard.word,translation:editTrans.trim()};
    setSrsCards(srsCards.map((c,i)=>i===srsIdx?updated:c));
    const proj=allProjects.find(p=>(p.cards||[]).some(c=>c.id===srsCard.id));
    if(proj&&onSaveProject)onSaveProject(proj.id,(proj.cards||[]).map(c=>c.id===srsCard.id?updated:c));
    setEditingCard(false);
  };
  const pg={minHeight:"100vh",background:P.backdrop,fontFamily:SANS,position:"relative"};
  const hdr={background:P.glassHdr,backdropFilter:"blur(12px)",borderBottom:`1px solid ${P.border}`,padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:10};

  // Повторение одно: экран сразу открывает очередь карточек. Раньше здесь был выбор
  // источника, направления и режима («Просмотр карточек», «Квиз») — лишние варианты.
  if(mode==="choose")return(
    <div style={pg}>
      <div role="status" aria-label={t("Загрузка...")} style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"60vh"}}>
        <div style={{width:40,height:40,border:`3px solid ${P.border}`,borderTopColor:P.blue,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      </div>
    </div>
  );

  // ── SRS mode ──────────────────────────────────────────────────────────────
  if(mode==="srs"){
    if(!srsCard)return null;
    const srsQ=dir==="word2trans"?srsCard.word:srsCard.translation||"—";
    const srsA=dir==="word2trans"?srsCard.translation||"—":srsCard.word;
    const srsProg=(srsIdx/Math.max(1,srsCards.length))*100;
    // Лицо карты: в «Аркаде» (P.face) — кремовая игральная карта с тёмным текстом, иначе — панель темы.
    const playing=themeKit(P).card==="playing"&&!!P.face;
    const F=playing?{...P,card:P.face.bg,text:P.face.text,mid:P.face.mid,faint:P.face.faint,border:P.face.border,surface:P.face.surface}:P;
    return(
      <div style={pg}>
        <div style={hdr} data-surface="glass">
          <button onClick={()=>{stopSpeaking();onBack();}} className="ac-ibtn" style={backLinkStyle(P)}><Icon n="arrowLeft" s={17}/>{t("Стоп")}</button>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:13,color:P.mid,fontWeight:600}}>{srsIdx+1} / {srsCards.length}</span>
            {/* Серия ×N — только в объёмной теме: красная плашка «множителя», подпрыгивает на каждый верный ответ. */}
            {P.lift>0&&combo>=2&&<span key={combo} data-combo="" title={t("Верных ответов подряд")} style={{fontSize:13,fontWeight:700,color:"#fff",background:P.dangerFill||P.danger,borderRadius:6,padding:"2px 8px",boxShadow:`0 3px 0 ${P.shadow}`}}>×{combo}</span>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <button data-btn="" onClick={cycleTts} title={t("Озвучка: {0}",[ttsMode==='front'?t("фронт"):ttsMode==='back'?t("бек"):t("выкл")])} aria-label={t("Озвучка")} style={{width:40,height:40,display:"inline-flex",alignItems:"center",justifyContent:"center",background:ttsMode==='off'?"none":P.bluePale,border:`1px solid ${ttsMode==='off'?P.border:"transparent"}`,color:ttsMode==='off'?P.faint:P.blueDark,borderRadius:8,padding:0,fontSize:13,cursor:"pointer",fontFamily:SANS}}>{ttsIcon}</button>
            {onOpenFsrsSettings&&<button data-btn="" onClick={onOpenFsrsSettings} title={t("Удержание: {0}%",[Math.round(fsrs.retention*100)])} aria-label={t("Настройки повторения")} className="ac-ibtn" style={{width:40,height:40,display:"inline-flex",alignItems:"center",justifyContent:"center",background:P.lift?P.surface:"none",border:`1px solid ${P.border}`,color:P.mid,borderRadius:8,padding:0,cursor:"pointer"}}><Icon n="settings" s={17}/></button>}
            
          </div>
        </div>
        <div style={{maxWidth:480,margin:"32px auto 0",padding:"0 16px calc(90px + var(--ac-mini-h, 0px))",position:"relative",zIndex:1}}>
          <div style={{height:4,background:P.border,borderRadius:2,marginBottom:28}}>
            <div data-meter="" style={{height:4,background:P.blue,borderRadius:2,width:`${srsProg}%`,transition:"width .3s"}}/>
          </div>
          {/* Edit modal */}
          {editingCard&&(
            <Modal size="sm" onClose={()=>setEditingCard(false)}
              title={<span style={{display:"inline-flex",alignItems:"center",gap:8}}><Icon n="edit" s={17}/>{t("Редактировать карточку")}</span>}
              footer={<><UiButton onClick={()=>setEditingCard(false)}>{t("Отмена")}</UiButton><UiButton variant="primary" onClick={saveEdit}>{t("Сохранить")}</UiButton></>}>
              <Field label={t("Слово / фраза")} autoFocus value={editWord} onChange={e=>setEditWord(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveEdit();}}/>
              <Field label={t("Перевод")} value={editTrans} onChange={e=>setEditTrans(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")saveEdit();}} style={{marginBottom:0}}/>
            </Modal>
          )}
          {/* Card */}
          <div style={{position:"relative",marginBottom:20}}>
            <button data-btn="" onClick={e=>{e.stopPropagation();setEditWord(srsCard.word);setEditTrans(srsCard.translation||"");setEditingCard(true);}}
              aria-label={t("Редактировать")} style={{position:"absolute",top:8,right:8,zIndex:2,width:36,height:36,display:"inline-flex",alignItems:"center",justifyContent:"center",background:F.lift?F.surface:"none",border:`1px solid ${F.border}`,color:F.mid,borderRadius:8,padding:0,cursor:"pointer",fontFamily:SANS}}
              title={t("Редактировать")}><Icon n="edit"/></button>
          {/* data-playcard: в «Аркаде» карта слегка покачивается, как в руке (index.css). */}
          <div data-playcard="" data-tilt="" data-face-card={playing?"":undefined} data-flipside={srsFlipped?"b":"f"} key={srsIdx} onClick={e=>{if(e.shiftKey)return;setSrsFlipped(f=>!f);}} style={{background:F.card,border:playing?`3px solid ${F.text}`:`2px solid ${cardColorIn(P,srsCard.color)||F.border}`,borderRadius:playing?14:9,padding:playing?"44px 30px":"40px 24px",textAlign:"center",cursor:"pointer",
            // Игральная карта, как в Balatro: тёмная толстая обводка, белая кромка внутри, тонкая
            // рамка цвета карточки и жёсткая тень-подложка снизу (карта лежит на столе).
            boxShadow:playing?`inset 0 0 0 3px #fffaf0, inset 0 0 0 6px ${F.card}, inset 0 0 0 8px ${cardColorIn(P,srsCard.color)||F.border}aa, 0 6px 0 rgba(0,0,0,.6), 0 18px 34px -6px rgba(0,0,0,.6), 0 0 22px -6px rgba(0,157,255,.5)`:`0 8px 28px ${P.shadowMd}`,minHeight:220,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",position:"relative"}}>
            {/* Игральная карта («Аркада», P.face): в углах достоинство над мастью, как у настоящей
                (нижний угол перевёрнут), в центре — крупная бледная масть под текстом. */}
            {playing&&(()=>{const su=SUIT[srsIdx%4],rk=RANK[srsIdx%RANK.length];
              const corner={position:"absolute",display:"flex",flexDirection:"column",alignItems:"center",gap:1,lineHeight:1,color:su.c,pointerEvents:"none"};
              return(<>
                <span aria-hidden="true" style={{...corner,top:12,left:14}}><b data-display="" style={{fontSize:22}}>{rk}</b><span style={{fontSize:16}}>{su.s}</span></span>
                <span aria-hidden="true" style={{...corner,bottom:12,right:14,rotate:"180deg"}}><b data-display="" style={{fontSize:22}}>{rk}</b><span style={{fontSize:16}}>{su.s}</span></span>
                {/* Водяной знак лежит под текстом нарочно — data-layout-allow для автопроверки. */}
                <span aria-hidden="true" data-layout-allow="cover" style={{position:"absolute",inset:0,display:"grid",placeItems:"center",fontSize:150,lineHeight:1,color:su.c,opacity:.07,zIndex:-1,pointerEvents:"none"}}>{su.s}</span>
              </>);})()}
            {/* data-flipside на карте: в «Аркаде» ответ появляется объёмным переворотом (index.css). */}
            {!srsFlipped?(
              <div data-face="" key="front" style={{display:"contents"}}>
                {(()=>{const st=srsCard.srs_state||"new";
                  const label=<>{srsDueLabel(srsCard)} · {st==="review"?t("повтор {0}д",[srsCard.srs_interval||0]):t("обучение")}</>;
                  if(!playing)return <div style={{fontSize:10,color:F.faint,textTransform:"uppercase",letterSpacing:".12em",marginBottom:14}}>{label}</div>;
                  const fill=st==="new"?P.blueMid:st==="review"?P.green:P.warm;const lb=labelOn(P,fill);
                  return <div data-display="" data-outline={lb.outline?"":undefined} style={{display:"inline-block",fontSize:12,color:lb.fg,background:fill,border:`2px solid ${F.text}`,borderRadius:8,padding:"3px 10px",marginBottom:14,boxShadow:`0 2px 0 ${F.text}`,textShadow:lb.outline?"1px 0 0 rgba(0,0,0,.55),-1px 0 0 rgba(0,0,0,.55),0 1px 0 rgba(0,0,0,.55),0 -1px 0 rgba(0,0,0,.55)":"none"}}>{label}</div>;})()}
                <div style={{...cardText(srsQ,30),color:srsCard.color?legibleOn(cardColorIn(P,srsCard.color),F.card):F.text,marginBottom:14,cursor:'text',userSelect:'text'}}>{srsQ}</div>
                <div style={{fontSize:11,color:F.faint,marginBottom:10}}>{t("нажмите, чтобы перевернуть")}</div>
                <UiIconButton C={F} icon="volume" label={t("Произнести")} size={36} iconSize={16} style={{borderRadius:18,color:F.faint}}
                  onClick={e=>{e.stopPropagation();sayFront(srsCard);}}/>
              </div>
            ):(
              <div data-face="" key="back" style={{display:"contents"}}>
                <div style={{...cardSmall,fontSize:13,color:F.mid,marginBottom:8,cursor:'text',userSelect:'text'}}>{srsQ}</div>
                <div style={{...cardText(srsA,26),color:F.text,marginBottom:12,cursor:'text',userSelect:'text'}}>{srsA}</div>
                <UiIconButton C={F} icon="volume" label={t("Произнести")} size={36} iconSize={16} style={{borderRadius:18,color:F.faint}}
                  onClick={e=>{e.stopPropagation();sayBack(srsCard);}}/>
              </div>
            )}
          </div>
          </div>{/* end card wrapper */}
          {/* Rating buttons — на телефоне прижаты к низу экрана (как в AnkiDroid) */}
          <div className="ac-quiz-actions" data-surface="glass" style={{"--ac-glass":P.glassHdr,"--ac-line":P.borderSoft}}>
          {!srsFlipped?(
            <UiButton variant="primary" size="lg" full onClick={()=>setSrsFlipped(true)} style={{minHeight:52,borderRadius:12,fontSize:15}}>{t("Показать ответ")}</UiButton>
          ):(
            <div style={{display:"grid",gridTemplateColumns:"repeat(4, minmax(0,1fr))",gap:6}}>
              {/* Плоские темы: панель с цветной полосой сверху. Объёмная («Аркада», P.lift):
                  сочная плашка своего цвета с ребром и бликом, как кнопки в Balatro. */}
              {[[1,t("Снова"),P.danger,P.dangerFill||P.danger],[2,t("Трудно"),P.warmDark,P.warm],[3,t("Хорошо"),P.green,P.green],[4,t("Легко"),P.blueDark,P.blueMid]].map(([r,label,col,fill])=>{const lb=labelOn(P,fill);return(
                <button key={r} onClick={()=>rateSrs(r)} className="ac-press" data-lift={P.lift?"":undefined} data-outline={P.lift&&lb.outline?"":undefined}
                  style={P.lift
                    ?{minHeight:56,background:fill,border:"none",color:lb.fg,"--ac-edge":shade(fill),padding:"8px 4px",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:SANS,textAlign:"center",minWidth:0}
                    :{minHeight:56,...panelSurface(P),borderTop:`3px solid ${col}`,color:col,padding:"8px 4px",borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer",fontFamily:SANS,textAlign:"center",minWidth:0}}>
                  <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</div>
                  <div data-plain="" style={{fontSize:11.5,color:P.lift?"inherit":P.faint,opacity:P.lift?0.9:1,marginTop:2,fontWeight:500}}>{srsNextLabel(srsCard,r,{retention:fsrs.retention})}</div>
                </button>
              );})}
            </div>
          )}
          <div className="ac-kbd-hint" style={{color:P.faint}}>{srsFlipped?t("1 · 2 · 3 · 4 — оценка, пробел — «Хорошо»"):t("Пробел — показать ответ")}</div>
          </div>
        </div>
      </div>
    );
  }

  if(mode==="srs_done"){
    const prog=dayProgress(getCards());
    const left=prog.total,done=prog.done,goal=prog.all;
    const streak=readStreak();
    const goalMet=left===0&&done>0;
    const mins=(n)=>Math.max(1,Math.round(n*8/60));
    const title=srsDone===0?(left?t("Сейчас повторять нечего"):t("На сегодня всё!")):goalMet?t("План на сегодня выполнен!"):t("Подход готов!");
    const sub=srsDone>0?t("Повторено: {0}",[nCards(srsDone)]):left?t("Карточки, которые вы сейчас учите, вернутся через несколько минут."):t("Новые карточки появятся завтра.");
    const tileSt={...panelSurface(P),borderRadius:12,padding:"12px 14px"};
    return(
      <div style={pg}>
        <div style={hdr} data-surface="glass"><button onClick={onBack} className="ac-ibtn" style={backLinkStyle(P)}><Icon n="arrowLeft" s={17}/>{t("Назад")}</button></div>
        <div style={{maxWidth:440,margin:"0 auto",padding:"36px 16px 48px",position:"relative",zIndex:1,textAlign:"center",fontFamily:SANS}}>
          <div style={{width:72,height:72,borderRadius:"50%",background:goalMet?P.greenPale:P.bluePale,color:goalMet?P.green:P.blueDark,display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:14}}>
            <Icon n={goalMet?"target":srsDone?"sparkles":"check"} s={34} sw={1.6}/>
          </div>
          <h1 style={{fontSize:24,fontWeight:700,color:P.text,margin:"0 0 6px",letterSpacing:"-0.01em"}}>{title}</h1>
          <div style={{fontSize:15,color:P.mid,marginBottom:20,lineHeight:1.5}}>{sub}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:18,textAlign:"left"}}>
            <div data-panel="" style={tileSt}>
              <div style={{fontSize:13,color:P.mid}}>{t("План на сегодня")}</div>
              <div style={{fontSize:22,fontWeight:700,color:P.text,fontVariantNumeric:"tabular-nums"}}>{done} <span style={{color:P.faint,fontWeight:500}}>/ {goal}</span></div>
              <div role="progressbar" aria-label={t("План на сегодня")} aria-valuemin={0} aria-valuemax={goal} aria-valuenow={done} style={{height:6,borderRadius:3,background:P.surface,marginTop:6,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${goal?Math.min(100,Math.round(done/goal*100)):100}%`,borderRadius:3,background:goalMet?P.green:P.blue,transition:"width .4s ease"}} data-meter=""/>
              </div>
            </div>
            <div data-panel="" style={tileSt}>
              <div style={{fontSize:13,color:P.mid,display:"flex",alignItems:"center",gap:6}}><span style={{color:streak.count>0?P.warm:P.faint,display:"inline-flex"}}><Icon n="flame" s={15}/></span>{t("Серия")}</div>
              <div style={{fontSize:22,fontWeight:700,color:P.text,fontVariantNumeric:"tabular-nums"}}>{nDays(streak.count)}</div>
              <div style={{fontSize:12.5,color:P.faint,marginTop:2}}>{t("рекорд {0}",[streak.best])}</div>
            </div>
          </div>
          <div style={{fontSize:14,color:P.mid,marginBottom:16}}>
            {left>0?t("Осталось на сегодня: {0} · ≈ {1} мин",[nCards(left),mins(left)]):t("Всё, что было на сегодня, повторено. Возвращайтесь завтра!")}
          </div>
          {/* Пиявок накопилось — предложить закрепить их в играх. */}
          <LeechNudge cards={getCards()} onGames={onGames} style={{marginBottom:14}}/>
          {left>0?(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <UiButton size="lg" full onClick={()=>onBack()}>{t("Хватит на сегодня")}</UiButton>
              <UiButton size="lg" full variant="primary" icon="play" onClick={startSrs}>{t("Ещё {0}",[Math.min(roundSize(),left)])}</UiButton>
            </div>
          ):(
            <UiButton size="lg" full variant="primary" onClick={()=>onBack()}>{t("Готово")}</UiButton>
          )}
        </div>
      </div>
    );
  }

  return null;
}
